import * as React from 'react';
import { RelatorioFluxoMoneyCell } from './relatorio-fluxo-money-cell';
import { RelatorioFluxoTooltip } from './relatorio-fluxo-tooltip';
import { RelatorioFluxoConsolidado } from './relatorio-fluxo-consolidado';
import {
  BaSeBreakpointContext,
  BaSeHeading5,
  BaSeIcon,
  BaSeShapeButton,
  BaSeSmall1,
  BaSeTheme,
  BaSeDialogContext,
  BaSeButton,
} from '@base/react';
import {
  MainBody,
  CardHeader,
  CardHeaderButtons,
  CardContentWrapper,
  CardLeftPanel,
  CardRightPanel,
  LeftInfoMain,
  RightInfoMain,
  RightInfoCell,
  RightCellContent,
  LeftButtonToggler,
  ExternalShell,
} from './relatorio-fluxo-lista-styled';
import { useRelatorio } from '../../../services/relatorio/RelatorioService';
import { ModalShare } from '../modal-share';
import { ShowButtonsAction } from '../head-buttons-action';
import { makeArrayFromObj } from '../../../pages/new-relatorios/new-relatorios';

const DEFAULT_TEXT_COLOR = BaSeTheme.colors.institucionais.cinzaSebrae20;
const DEFAULT_SUB_TEXT_COLOR = BaSeTheme.colors.institucionais.cinzaSebrae45;

const ICONS = {
  ANGLE_DOWN: 'angle-down',
  ANGLE_RIGHT: 'angle-right',
};

const MAP_MONTH_TO_ABR = {
  Jan: '01',
  Fev: '02',
  Mar: '03',
  Abr: '04',
  Maio: '05',
  Jun: '06',
  Jul: '07',
  Ago: '08',
  Set: '09',
  Out: '10',
  Nov: '11',
  Dez: '12',
};

export const RelatorioFluxoLista = ({
  lancamentos,
  download,
  loadingDownload,
  startDateBroken,
  endDateBroken,
  paramsFilter,
  isPublic,
  mensal = null,
  // eslint-disable-next-line
}) => {
  const { inTablet } = React.useContext(BaSeBreakpointContext);
  const { compartilharRelatorio } = useRelatorio();
  const { showModal } = React.useContext(BaSeDialogContext);

  const [menuOpen, setMenuOpen] = React.useState(true);
  const [mappedReceitaLancamentos, setMappedReceitaLancamentos] =
    React.useState(null);
  const [mappedDespesaLancamentos, setMappedDespesaLancamentos] =
    React.useState(null);

  const [showOptions, setShowOptions] = React.useState({
    Receita: true,
    Despesa: true,
  });
  // const [showSubOption, setShowSubOptions] = React.useState({});

  const [daysOrMonths, setDaysOrMonths] = React.useState(null);

  const [saldoPeriodoList, setSaldoPeriodoList] = React.useState(null);
  const [saldoTotalList, setSaldoTotalList] = React.useState(null);

  const [totalReceitas, setTotalReceitas] = React.useState(null);
  const [totalDespesas, setTotalDespesas] = React.useState(null);

  const [checkDataList, setCheckDataList] = React.useState(null);
  const [isMensal, setIsMensal] = React.useState();

  const [consolidado, setConsolidado] = React.useState(null);

  const print = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.print();
  };

  const toggleOption = React.useCallback((key) => {
    setShowOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const convertDate = React.useCallback(
    (date) => {
      if (date.includes('/')) return date;

      if (isMensal) {
        const [year, month] = date.split('-');
        return `${month}/${year}`;
      }

      const [, month, day] = date.split('-');
      return `${day}/${month}`;
    },
    [isMensal],
  );

  const truncateText = React.useCallback(
    (text, isShort) => {
      if (!text) return '';

      const maxLength = inTablet ? (isShort ? 23 : 28) : isShort ? 10 : 15;

      return text.length > maxLength
        ? `${text.substring(0, maxLength)} ...`
        : text;
    },
    [inTablet],
  );

  const renderMoneyRow = React.useCallback(
    (values, type) => (
      <RightInfoMain height="32px">
        {values?.map((value, index) => (
          <RightInfoCell blue height="32px" key={index}>
            <RightCellContent>
              <RelatorioFluxoMoneyCell type={type} value={value} />
            </RightCellContent>
          </RightInfoCell>
        ))}
      </RightInfoMain>
    ),
    [],
  );

  const normalizeDataByDates = React.useCallback(
    (items) => {
      if (!items || !checkDataList) return [];

      // Agrupa valores por data
      const valuesByDate = items.reduce((acc, item) => {
        const date = convertDate(item.dataCadastro);
        acc[date] = (acc[date] || 0) + item.valor;
        return acc;
      }, {});

      // Preenche datas faltantes com zero
      return Object.keys(checkDataList).map((date) => valuesByDate[date] || 0);
    },
    [checkDataList, convertDate],
  );

  // Transforma categorias em estrutura hierárquica
  const buildCategoryHierarchy = React.useCallback((categories) => {
    if (!categories) return [];

    return Object.entries(categories).map(([categoryName, categoryData]) => {
      const subcategories = Object.entries(categoryData.subCate || {}).map(
        ([subName, subItems]) => ({
          name: subName,
          items: subItems.map((item) => item.descricao),
        }),
      );

      return {
        name: categoryName,
        subcategories,
      };
    });
  }, []);

  // Renderiza valores monetários das categorias
  const renderCategoryValues = React.useCallback(
    (categories, type) => {
      if (!categories) return null;

      return Object.entries(categories).map(
        ([categoryKey, categoryData], index) => {
          const categoryValues = normalizeDataByDates(categoryData.data);
          const subcategories = Object.entries(categoryData.subCate || {});

          return (
            <React.Fragment key={index}>
              {showOptions[type] && (
                <>
                  <RightInfoMain height="32px">
                    {categoryValues?.map((value, idx) => (
                      <RightInfoCell height="32px" key={idx}>
                        <RightCellContent>
                          <RelatorioFluxoMoneyCell type={type} value={value} />
                        </RightCellContent>
                      </RightInfoCell>
                    ))}
                  </RightInfoMain>

                  {subcategories.map(([subKey, subItems], subIndex) => {
                    const subValues = normalizeDataByDates(subItems);
                    const itemValues = subItems.map((item) =>
                      normalizeDataByDates([item]),
                    );

                    return (
                      <React.Fragment key={subIndex}>
                        <RightInfoMain
                          hide={!showOptions[categoryKey]}
                          height="32px"
                        >
                          {subValues?.map((value, idx) => (
                            <RightInfoCell height="32px" key={idx}>
                              <RightCellContent>
                                <RelatorioFluxoMoneyCell
                                  isSub
                                  type={type}
                                  value={value}
                                />
                              </RightCellContent>
                            </RightInfoCell>
                          ))}
                        </RightInfoMain>

                        {itemValues?.map((itemRow, itemIndex) => (
                          <RightInfoMain
                            hide={
                              !showOptions[categoryKey] ||
                              !showOptions[`${categoryKey}${subKey}`]
                            }
                            height="32px"
                            key={itemIndex}
                          >
                            {itemRow?.map((value, idx) => (
                              <RightInfoCell height="32px" key={idx}>
                                <RightCellContent>
                                  <RelatorioFluxoMoneyCell
                                    isSub
                                    type={type}
                                    value={value}
                                  />
                                </RightCellContent>
                              </RightInfoCell>
                            ))}
                          </RightInfoMain>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </React.Fragment>
          );
        },
      );
    },
    [normalizeDataByDates, showOptions],
  );

  // Renderiza hierarquia de categorias (painel esquerdo)
  const renderCategoryHierarchy = React.useCallback(
    (categories, type) => {
      const hierarchy = buildCategoryHierarchy(categories);

      return hierarchy.map(({ name, subcategories }, index) => (
        <React.Fragment key={index}>
          {showOptions[type] && (
            <>
              <LeftInfoMain
                onClick={() => toggleOption(name)}
                hasClick
                height="32px"
              >
                <BaSeIcon
                  description={`Exibe/Esconde subcategorias de ${name}`}
                  color="#617385"
                  size={14}
                  name={
                    showOptions[name] ? ICONS.ANGLE_RIGHT : ICONS.ANGLE_DOWN
                  }
                />
                <RelatorioFluxoTooltip message={name}>
                  <BaSeSmall1 color={DEFAULT_TEXT_COLOR}>
                    {truncateText(name, subcategories?.length)}
                  </BaSeSmall1>
                </RelatorioFluxoTooltip>
              </LeftInfoMain>

              {subcategories.map(({ name: subName, items }, subIndex) => (
                <React.Fragment key={subIndex}>
                  <LeftInfoMain
                    onClick={() => toggleOption(`${name}${subName}`)}
                    hasClick
                    hide={!showOptions[name]}
                    height="32px"
                  >
                    <BaSeIcon
                      description="create space"
                      color={DEFAULT_SUB_TEXT_COLOR}
                      size={14}
                      name={
                        showOptions[`${name}${subName}`] && showOptions[name]
                          ? ICONS.ANGLE_RIGHT
                          : ICONS.ANGLE_DOWN
                      }
                    />
                    <RelatorioFluxoTooltip message={subName}>
                      <BaSeSmall1 color={DEFAULT_SUB_TEXT_COLOR}>
                        {truncateText(subName, true)}
                      </BaSeSmall1>
                    </RelatorioFluxoTooltip>
                  </LeftInfoMain>

                  {items.map((item, itemIndex) => (
                    <LeftInfoMain
                      key={itemIndex}
                      hide={
                        !showOptions[name] || !showOptions[`${name}${subName}`]
                      }
                      height="32px"
                    >
                      <BaSeIcon
                        description="create space"
                        color="#fff"
                        size={14}
                        name="linux"
                      />
                      <RelatorioFluxoTooltip message={item}>
                        <BaSeSmall1 color={DEFAULT_SUB_TEXT_COLOR}>
                          {truncateText(item, true)}
                        </BaSeSmall1>
                      </RelatorioFluxoTooltip>
                    </LeftInfoMain>
                  ))}
                </React.Fragment>
              ))}
            </>
          )}
        </React.Fragment>
      ));
    },
    [buildCategoryHierarchy, showOptions, toggleOption, truncateText],
  );

  const receitasHierarchy = React.useMemo(
    () => renderCategoryHierarchy(mappedReceitaLancamentos, 'Receita'),
    [mappedReceitaLancamentos, renderCategoryHierarchy],
  );

  const despesasHierarchy = React.useMemo(
    () => renderCategoryHierarchy(mappedDespesaLancamentos, 'Despesa'),
    [mappedDespesaLancamentos, renderCategoryHierarchy],
  );

  const receitasValues = React.useMemo(
    () => renderCategoryValues(mappedReceitaLancamentos, 'Receita'),
    [mappedReceitaLancamentos, renderCategoryValues],
  );

  const despesasValues = React.useMemo(
    () => renderCategoryValues(mappedDespesaLancamentos, 'Despesa'),
    [mappedDespesaLancamentos, renderCategoryValues],
  );

  const groupByCategory = React.useCallback((accumulator, transaction) => {
    const categoryName = transaction.nomeCategoria;

    if (!accumulator[categoryName]) {
      accumulator[categoryName] = { subCate: {}, data: [] };
    }

    transaction.listaCategoria.forEach((category) => {
      const subCategoryName = category.nomeSubCategoria;
      const items = category.listaSubCategoria;

      if (!accumulator[categoryName].subCate[subCategoryName]) {
        accumulator[categoryName].subCate[subCategoryName] = [];
      }

      accumulator[categoryName].subCate[subCategoryName].push(...items);
      accumulator[categoryName].data.push(...items);
    });
  }, []);

  const renderRow = React.useCallback(
    (values, type, { noBorder, height = '32px', blue, last } = {}) => (
      <>
        {values?.map((value, index) => (
          <RightInfoCell
            noBorder={noBorder}
            height={height}
            blue={blue}
            key={index}
            last={last}
          >
            <RightCellContent>
              <RelatorioFluxoMoneyCell type={type} value={value} />
            </RightCellContent>
          </RightInfoCell>
        ))}
      </>
    ),
    [],
  );

  const padZero = (value) => value.padStart(2, '0');

  const parseLabelDate = React.useCallback((dateLabel) => {
    const isMonthly = dateLabel.length >= 8;
    setIsMensal(isMonthly);

    let formattedDate;
    if (isMonthly) {
      const [month, year] = dateLabel.split('/');
      formattedDate = `${MAP_MONTH_TO_ABR[month]}/${year}`;
    } else {
      const [day, month] = dateLabel.split('/');
      formattedDate = `${padZero(day)}/${MAP_MONTH_TO_ABR[month]}`;
    }

    setCheckDataList((prev) => ({ ...prev, [formattedDate]: true }));
    return formattedDate;
  }, []);

  const getBrokenDateIndicator = React.useCallback(
    (index) => {
      if (!isMensal || !daysOrMonths) return '';

      const isFirst = index === 0 && startDateBroken;
      const isLast = index === daysOrMonths.length - 1 && endDateBroken;

      return isFirst || isLast ? '*' : '';
    },
    [daysOrMonths, endDateBroken, isMensal, startDateBroken],
  );

  const dateHeaders = React.useMemo(
    () => (
      <>
        {daysOrMonths?.map((dateInfo, index) => (
          <RightInfoCell height="46px" key={dateInfo.label}>
            <RightCellContent>
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold>
                {dateInfo.label} {getBrokenDateIndicator(index)}
              </BaSeSmall1>
            </RightCellContent>
          </RightInfoCell>
        ))}
      </>
    ),
    [daysOrMonths, getBrokenDateIndicator],
  );

  React.useEffect(() => {
    if (!lancamentos) return;

    setCheckDataList({});

    const receitas = {};
    const despesas = {};
    const dates = [];
    const periodBalances = [];
    const totalBalances = [];
    const revenuesByDate = [];
    const expensesByDate = [];

    const transactionsList = makeArrayFromObj(lancamentos.lancamentos);

    transactionsList.forEach((dailyData) => {
      dates.push({
        label: dailyData.data,
        mapper: parseLabelDate(dailyData.data),
        mensal: dailyData.data.length >= 8,
      });

      periodBalances.push(dailyData.saldoPeriodo);
      totalBalances.push(dailyData.saldoFinal);
      revenuesByDate.push(dailyData.totalReceitas);
      expensesByDate.push(dailyData.totalDespesas);

      dailyData.listaDespesa.forEach((expense) =>
        groupByCategory(despesas, expense),
      );
      dailyData.listaReceita.forEach((revenue) =>
        groupByCategory(receitas, revenue),
      );
    });

    setMappedDespesaLancamentos(despesas);
    setMappedReceitaLancamentos(receitas);
    setDaysOrMonths(dates);
    setSaldoPeriodoList(periodBalances);
    setSaldoTotalList(totalBalances);
    setTotalDespesas(expensesByDate);
    setTotalReceitas(revenuesByDate);
    setConsolidado(lancamentos);
  }, [parseLabelDate, groupByCategory, lancamentos]);

  const handleShowModal = React.useCallback(
    () =>
      showModal({
        title: 'Compartilhar',
        actions: [],
        size: 'medium',
        content: ({ closeModal }) => (
          <ModalShare
            closeModal={closeModal}
            filters={paramsFilter}
            callback={compartilharRelatorio}
            tipoRelatorio={'RELATORIO_FLUXO'}
            isMensal={mensal}
          />
        ),
      }),
    [showModal, paramsFilter, compartilharRelatorio, mensal],
  );

  return (
    <ExternalShell>
      <MainBody>
        <CardHeader>
          <BaSeHeading5 color="#243342">Fluxo de caixa</BaSeHeading5>
          <CardHeaderButtons>
            {!isPublic && (
              <ShowButtonsAction
                download={download}
                loadingDownload={loadingDownload}
                print={print}
                isDisabled={false}
                showModal={handleShowModal}
              />
            )}
            {isPublic &&
              (inTablet ? (
                <BaSeButton
                  value="Imprimir"
                  leftIcon="print"
                  onClick={print}
                  type="secondary"
                />
              ) : (
                <BaSeShapeButton
                  shape="square"
                  nameIcon="print"
                  onClick={print}
                  type="secondary"
                />
              ))}
          </CardHeaderButtons>
        </CardHeader>
        <CardContentWrapper>
          <CardLeftPanel isOpen={menuOpen}>
            {!inTablet && (
              <LeftButtonToggler>
                <BaSeShapeButton
                  onClick={() => setMenuOpen((prev) => !prev)}
                  nameIcon={menuOpen ? 'angle-left' : ICONS.ANGLE_RIGHT}
                  type="secondary"
                  size="small"
                  shape="square"
                />
              </LeftButtonToggler>
            )}
            <LeftInfoMain height="46px">
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold>
                {!inTablet ? 'Categorias' : 'Categorias de lançamentos'}
              </BaSeSmall1>
            </LeftInfoMain>
            <LeftInfoMain
              hasClick
              onClick={() => toggleOption('Receita')}
              height="32px"
              blue
            >
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold>
                Receitas
              </BaSeSmall1>
              <BaSeIcon
                name={
                  showOptions['Receita'] ? 'arrow-head-up' : 'arrow-head-down'
                }
                description="Exibe/Recolhe informações de lançamentos de Receitas"
                size={12}
                color={BaSeTheme.colors.institucionais.azulSebrae36}
              />
            </LeftInfoMain>
            {receitasHierarchy}
            <LeftInfoMain
              hasClick
              onClick={() => toggleOption('Despesa')}
              height="32px"
              blue
            >
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold>
                Despesas
              </BaSeSmall1>
              <BaSeIcon
                name={
                  showOptions['Despesa'] ? 'arrow-head-up' : 'arrow-head-down'
                }
                description="Exibe/Recolhe informações de lançamentos de Despesas"
                size={12}
                color={BaSeTheme.colors.institucionais.azulSebrae36}
              />
            </LeftInfoMain>
            {despesasHierarchy}
            <LeftInfoMain height="32px">
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold>
                Saldo do período
              </BaSeSmall1>
            </LeftInfoMain>
            <LeftInfoMain height="46px" blue last>
              <BaSeSmall1 color={DEFAULT_TEXT_COLOR} isBold>
                Saldo total
              </BaSeSmall1>
            </LeftInfoMain>
          </CardLeftPanel>
          <CardRightPanel>
            <RightInfoMain height="46px">{dateHeaders}</RightInfoMain>
            {renderMoneyRow(totalReceitas, 'Receita')}
            {receitasValues}
            {renderMoneyRow(totalDespesas, 'Despesa')}
            {despesasValues}
            <RightInfoMain height="32px">
              {renderRow(saldoPeriodoList, 'auto')}
            </RightInfoMain>
            <RightInfoMain height="36px">
              {renderRow(saldoTotalList, 'auto', {
                noBorder: true,
                height: '36px',
                blue: true,
                last: true,
              })}
            </RightInfoMain>
          </CardRightPanel>
        </CardContentWrapper>
      </MainBody>
      <RelatorioFluxoConsolidado
        dados={consolidado}
        broken={isMensal && (startDateBroken || endDateBroken)}
      />
    </ExternalShell>
  );
};
